// Tabelas de preço no MeuFrete (PO1-2312): a transportadora envia a tabela
// (PDF, Excel ou imagem) para o embarcador escolhido no topo e acompanha o
// andamento: recebida, em cadastro, cadastrada (aguardando aprovação do
// embarcador), aprovada ou devolvida com motivo.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import TablePagination from "components/TablePagination";
import { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import { enviarTabela, listarTabelasEnviadas, urlArquivoTabela, mensagemErro } from "services/meuFreteApi";
import { formatDateTime } from "utils/formatDate";
import EscolherArquivos from "components/EscolherArquivos";

export const ESTADO_LABEL = {
  RECEBIDA: { label: "Recebida pela Everlog", tipo: "auto" },
  EM_CADASTRO: { label: "Em cadastro", tipo: "auto" },
  CADASTRADA: { label: "Aguardando aprovação do embarcador", tipo: "pend" },
  APROVADA: { label: "Aprovada e ativa", tipo: "done" },
  DEVOLVIDA: { label: "Devolvida", tipo: "lock" },
};
const ACEITOS = ".pdf,.xls,.xlsx,.xlsm,.png,.jpg,.jpeg";
const MAX_ARQUIVOS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };

function fmtVigencia(item) {
  const d = (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "");
  if (item.vigenciaInicio && item.vigenciaFim) return `${d(item.vigenciaInicio)} a ${d(item.vigenciaFim)}`;
  if (item.vigenciaInicio) return `a partir de ${d(item.vigenciaInicio)}`;
  if (item.vigenciaFim) return `até ${d(item.vigenciaFim)}`;
  return "—";
}

export default function TabelasEnviadas() {
  const { addToast } = useToast();
  const { empresa, isAllEmpresas } = useCompanies();
  const embarcadorId = empresa && !isAllEmpresas ? empresa.value : null;

  const [arquivos, setArquivos] = useState([]);
  const [vigenciaInicio, setVigenciaInicio] = useState("");
  const [vigenciaFim, setVigenciaFim] = useState("");
  const [observacao, setObservacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const [dados, setDados] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (embarcadorId) params.embarcadorId = embarcadorId;
      const r = await listarTabelasEnviadas(params);
      setDados({ items: r.items || [], total: Number(r.total) || 0 });
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Erro ao listar os envios", description: mensagemErro(err) });
    } finally {
      setLoading(false);
    }
  }, [empresa, embarcadorId, page, pageSize, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const escolherArquivos = (files) => {
    const lista = Array.from(files || []);
    if (lista.length > MAX_ARQUIVOS) {
      addToast({ place: "tr", type: "danger", title: "Limite excedido", description: `No máximo ${MAX_ARQUIVOS} arquivos por envio.` });
      setInputKey((k) => k + 1);
      return;
    }
    const grande = lista.find((f) => f.size > MAX_BYTES);
    if (grande) {
      addToast({ place: "tr", type: "danger", title: "Arquivo acima de 10 MB", description: grande.name });
      setInputKey((k) => k + 1);
      return;
    }
    setArquivos(lista);
  };

  const enviar = async () => {
    if (!embarcadorId || !arquivos.length) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      arquivos.forEach((f) => fd.append("Arquivos", f));
      fd.append("embarcadorId", String(embarcadorId));
      if (vigenciaInicio) fd.append("vigenciaInicio", vigenciaInicio);
      if (vigenciaFim) fd.append("vigenciaFim", vigenciaFim);
      if (observacao.trim()) fd.append("observacao", observacao.trim());
      await enviarTabela(fd);
      addToast({ place: "tr", type: "success", title: "Tabela enviada", description: "A Everlog recebeu a tabela e vai cadastrá-la. Você acompanha o andamento aqui." });
      setArquivos([]);
      setVigenciaInicio("");
      setVigenciaFim("");
      setObservacao("");
      setInputKey((k) => k + 1);
      setPage(1);
      carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível enviar", description: mensagemErro(err) });
    } finally {
      setEnviando(false);
    }
  };

  const abrirArquivo = async (item, idArquivo) => {
    try {
      const { url } = await urlArquivoTabela(item.id, idArquivo);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível abrir o arquivo", description: mensagemErro(err) });
    }
  };

  return (
    <div className="content">
      <PageHero
        eyebrow="MeuFrete"
        title="Tabelas de preço"
        description="Envie a tabela de preço acordada com o embarcador. A Everlog cadastra a tabela no sistema e o embarcador aprova; só depois ela passa a valer na auditoria dos CT-es."
        actions={<PageHeroRefreshButton onClick={carregar} loading={loading} />}
      />

      <ShellCard eyebrow="Novo envio" title={embarcadorId ? `Enviar tabela para ${empresa.label}` : "Enviar tabela"} description="PDF, Excel (xls, xlsx, xlsm) ou imagem (png, jpg). Até 5 arquivos de 10 MB.">
        {!embarcadorId ? (
          <div style={stateBoxStyle}>Escolha um embarcador específico no topo da página para enviar.</div>
        ) : (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label style={labelStyle}>Arquivos *</Label>
                  {/* PO1-3887: o tema esconde o input file dentro de .form-group. */}
                  <EscolherArquivos
                    key={inputKey}
                    multiple
                    accept={ACEITOS}
                    disabled={enviando}
                    ariaLabel="Arquivos da tabela de preço"
                    onSelecionar={escolherArquivos}
                  />
                  {arquivos.length > 0 && <div style={{ ...mutedStyle, marginTop: 6 }}>{arquivos.map((f) => f.name).join(", ")}</div>}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label style={labelStyle}>Vigência de</Label>
                  <Input type="date" value={vigenciaInicio} onChange={(e) => setVigenciaInicio(e.target.value)} disabled={enviando} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label style={labelStyle}>Vigência até</Label>
                  <Input type="date" value={vigenciaFim} onChange={(e) => setVigenciaFim(e.target.value)} disabled={enviando} />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label style={labelStyle}>Observação (até 1000 caracteres)</Label>
              <Input type="textarea" rows={2} value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} disabled={enviando} />
            </FormGroup>
            <Button color="info" size="sm" onClick={enviar} disabled={enviando || !arquivos.length}>
              {enviando ? <Spinner size="sm" /> : "Enviar tabela"}
            </Button>
          </>
        )}
      </ShellCard>

      <ShellCard eyebrow="Andamento" title="Tabelas enviadas" description={embarcadorId ? `Envios para ${empresa.label}.` : "Envios para todos os embarcadores."}>
        {loading && !dados.items.length ? (
          <div style={stateBoxStyle}><Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /><span>Carregando…</span></div>
        ) : dados.items.length === 0 ? (
          <div style={stateBoxStyle}>Nenhuma tabela enviada ainda.</div>
        ) : (
          <>
            <Table size="sm" responsive hover className="hub-thead-accent">
              <thead className="text-primary">
                <tr><th>Enviada em</th><th>Embarcador</th><th>Arquivos</th><th>Vigência</th><th>Situação</th><th>Tabela cadastrada</th></tr>
              </thead>
              <tbody>
                {dados.items.map((item) => {
                  const est = ESTADO_LABEL[item.estado] || { label: item.estado, tipo: "auto" };
                  return (
                    <tr key={item.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(item.creationTime)}</td>
                      <td>{item.embarcador}</td>
                      <td>
                        <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => abrirArquivo(item, 1)} title="O primeiro arquivo abre direto; os demais estão listados">{item.arquivos || "—"}</span>
                      </td>
                      <td>{fmtVigencia(item)}</td>
                      <td>
                        <Badge tipo={est.tipo}>{est.label}</Badge>
                        {item.estado === "DEVOLVIDA" && item.motivoDevolucao && (
                          <div style={{ ...mutedStyle, color: AUTOPILOT_COLORS.statusDanger }}>Motivo: {item.motivoDevolucao}</div>
                        )}
                      </td>
                      <td>{item.tabelaNome ? `${item.tabelaNome} (#${item.idFreteTabela})` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <TablePagination page={page} pageSize={pageSize} total={dados.total} pageSizes={[10, 20, 50]} itemLabel="envio(s)" onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
          </>
        )}
        <SectionCard variant="field" style={{ marginTop: "1rem" }}>
          <div style={mutedStyle}>
            Situações: <strong>Recebida</strong> (na fila da Everlog) → <strong>Em cadastro</strong> → <strong>Aguardando aprovação do embarcador</strong> (a tabela existe, inativa) → <strong>Aprovada e ativa</strong>. Uma tabela <strong>devolvida</strong> traz o motivo; corrija e envie de novo.
          </div>
        </SectionCard>
      </ShellCard>
    </div>
  );
}
